import { cn, UzButton, UzPopover } from '@utazzo/ui';
import { CalendarRange, ChevronLeft, ChevronRight } from 'lucide-react';
import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  describeMonths,
  istCurrentMonth,
  MAX_MONTHS,
  MONTH_NAMES_SHORT,
  monthKey,
  parseMonthsParam,
  recentMonths,
  serialiseMonths,
} from '../month-selection';

import { useUrlParamWriter } from '@/hooks/use-url-param-writer';

/**
 * Month multi-select for the Marketing report, URL-driven via `?months=`.
 *
 * The report only ever works in whole months (see `month-selection.ts`), and
 * the months need not be adjacent - comparing July against May without June is
 * a real ask, and a day range cannot express it.
 *
 * Picks are STAGED and committed on Apply rather than applied per click: the
 * page fans out to seven aggregates plus the cost gate, so refetching on every
 * toggle would spend a request on selections the operator is still assembling.
 */

/** Quick picks, all anchored to the current IST month. */
const PRESETS: ReadonlyArray<{ label: string; months: () => string[] }> = [
  { label: 'This month', months: () => recentMonths(1) },
  { label: 'Last 3 months', months: () => recentMonths(3) },
  { label: 'Last 6 months', months: () => recentMonths(6) },
  { label: 'Last 12 months', months: () => recentMonths(12) },
];

export const MonthFilter = (): React.JSX.Element => {
  const [params] = useSearchParams();
  const writeParams = useUrlParamWriter();
  const selected = parseMonthsParam(params.get('months'));

  const currentMonth = istCurrentMonth();
  const [open, setOpen] = useState(false);
  // Staged selection. Re-seeded from the URL each time the popover opens, so
  // abandoning a half-made selection (click outside) simply discards it.
  const [draft, setDraft] = useState<string[]>(selected);
  const [year, setYear] = useState<number>(Number(selected[selected.length - 1]!.slice(0, 4)));

  const onOpenChange = (next: boolean): void => {
    if (next) {
      const fromUrl = parseMonthsParam(params.get('months'));
      setDraft(fromUrl);
      setYear(Number(fromUrl[fromUrl.length - 1]!.slice(0, 4)));
    }
    setOpen(next);
  };

  const toggle = (month: string): void => {
    setDraft((prev) =>
      prev.includes(month)
        ? prev.filter((entry) => entry !== month)
        : [...prev, month].sort().slice(-MAX_MONTHS),
    );
  };

  const apply = (months: string[]): void => {
    // Merges into the LIVE URL so grid state written by nuqs survives.
    writeParams((p) => {
      p.set('months', serialiseMonths(months));
      // Leftovers from the shared day-range filter would otherwise sit in the
      // URL looking authoritative while this page ignores them.
      p.delete('daterange');
      p.delete('from');
      p.delete('to');
    });
    setOpen(false);
  };

  const draftYearMonths = draft.filter((month) => month.startsWith(`${year}-`));

  return (
    <UzPopover
      open={open}
      onOpenChange={onOpenChange}
      align="end"
      className="w-76 p-0"
      trigger={
        <UzButton variant="outline" size="sm" className="h-9">
          <CalendarRange className="mr-2 h-4 w-4" />
          {describeMonths(selected)}
        </UzButton>
      }
    >
      <div className="flex flex-col">
        <div className="flex flex-wrap gap-1 border-b border-(--border) p-2">
          {PRESETS.map((preset) => (
            <button
              key={preset.label}
              type="button"
              onClick={() => {
                const months = preset.months();
                setDraft(months);
                setYear(Number(months[months.length - 1]!.slice(0, 4)));
              }}
              className="text-muted-foreground hover:text-foreground hover:bg-muted rounded-full px-2.5 py-1 text-xs font-medium transition-colors"
            >
              {preset.label}
            </button>
          ))}
        </div>

        <div className="flex items-center justify-between px-3 py-2">
          <UzButton
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            onClick={() => setYear((value) => value - 1)}
            aria-label="Previous year"
          >
            <ChevronLeft className="h-4 w-4" />
          </UzButton>
          <span className="text-foreground text-sm font-semibold">{year}</span>
          <UzButton
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            onClick={() => setYear((value) => value + 1)}
            // Nothing to report past the current IST month.
            disabled={year >= Number(currentMonth.slice(0, 4))}
            aria-label="Next year"
          >
            <ChevronRight className="h-4 w-4" />
          </UzButton>
        </div>

        <div className="grid grid-cols-3 gap-1 px-3 pb-3">
          {MONTH_NAMES_SHORT.map((name, index) => {
            const month = monthKey(year, index);
            const isPicked = draft.includes(month);
            // Future months have no leads and no spend - offering them would
            // only ever add an empty column that locks the cost gate.
            const isFuture = month > currentMonth;
            return (
              <button
                key={month}
                type="button"
                disabled={isFuture}
                aria-pressed={isPicked}
                onClick={() => toggle(month)}
                className={cn(
                  'rounded-md border px-2 py-1.5 text-sm font-medium transition-colors',
                  isPicked
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'text-foreground hover:bg-muted border-(--border)',
                  isFuture &&
                    'text-muted-foreground cursor-not-allowed opacity-40 hover:bg-transparent',
                  month === currentMonth && !isPicked && 'border-primary/50',
                )}
              >
                {name}
              </button>
            );
          })}
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-(--border) px-3 py-2">
          <span className="text-muted-foreground text-xs">
            {draft.length === 0
              ? 'Pick at least one month'
              : `${draft.length} selected${
                  draftYearMonths.length > 0 && draftYearMonths.length < draft.length
                    ? ` (${draftYearMonths.length} in ${year})`
                    : ''
                }`}
          </span>
          <div className="flex items-center gap-1">
            <UzButton
              variant="ghost"
              size="sm"
              onClick={() => setDraft([])}
              disabled={draft.length === 0}
            >
              Clear
            </UzButton>
            <UzButton size="sm" onClick={() => apply(draft)} disabled={draft.length === 0}>
              Apply
            </UzButton>
          </div>
        </div>
      </div>
    </UzPopover>
  );
};
